import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import MembershipSetup from "@/components/MembershipSetup";
import BuildTaskForm from "@/components/BuildTaskForm";

const min2hm = (v) => `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;

export default function TasksPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [types, setTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [unitMembers, setUnitMembers] = useState([]);
  const [cfg, setCfg] = useState({ modes: [], sequence_groups: [], sequences: [] });
  const [tasks, setTasks] = useState([]);
  const [equip, setEquip] = useState([]);
  const [orgTree, setOrgTree] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [locs, setLocs] = useState([]);
  const [editId, setEditId] = useState(null);
  const [prefill, setPrefill] = useState(null);

  const loadTasks = () => {
    const to = new Date();
    to.setDate(to.getDate() + 14);
    const from = new Date();
    from.setDate(from.getDate() - 13);
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get(`/tasks?from_date=${iso(from)}&to_date=${iso(to)}`).then((r) => setTasks(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/task-types?company_id=${user.company_id}`).then((r) => setTypes(r.data)).catch(() => {});
    api.get(`/roles?company_id=${user.company_id}`).then((r) => setRoles(r.data)).catch(() => {});
    api.get("/org/unit-members").then((r) => setUnitMembers(r.data)).catch(() => {});
    api.get(`/task-config?company_id=${user.company_id}`).then((r) => setCfg(r.data)).catch(() => {});
    api.get(`/equipment-config?company_id=${user.company_id}`).then((r) => setEquip(r.data.equipment || [])).catch(() => {});
    api.get(`/locations?company_id=${user.company_id}`).then((r) => setLocs(r.data)).catch(() => {});
    api.get(`/org/tree?company_id=${user.company_id}`).then((r) => setOrgTree(r.data)).catch(() => {});
    api.get(`/org/members?company_id=${user.company_id}`).then((r) => setOrgMembers(r.data.members || [])).catch(() => {});
    loadTasks();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && !editId) setEditId(eid);
    if (searchParams.get("new")) {
      const d = searchParams.get("date");
      const s = searchParams.get("start");
      if (d && s) {
        const [h, m] = s.split(":").map(Number);
        setEditId(null);
        setPrefill({ date: d, start: s, end: min2hm(Math.min(1439, h * 60 + m + 60)), _ts: Date.now() });
      }
    }
  }, [searchParams, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const myUnit = user?.org?.unit_id;
  const mySub = user?.org?.subunit_id;
  const myTeam = user?.org?.team_id;
  const myEquip = equip.filter((e) => (!e.unit_id || e.unit_id === myUnit) && (!e.subunit_id || e.subunit_id === mySub) && (!e.team_id || e.team_id === myTeam));
  const taskLocs = locs.filter((l) => l.task_selectable !== false);

  const unitNameById = {}; const subNameById = {}; const teamNameById = {};
  for (const u of orgTree?.units || []) {
    unitNameById[u.id] = u.name;
    for (const s of u.subunits || []) { subNameById[s.id] = s.name; for (const tm of s.teams || []) teamNameById[tm.id] = tm.name; }
  }
  const orgLabelFor = (o) => [unitNameById[o?.unit_id], subNameById[o?.subunit_id], teamNameById[o?.team_id]].filter(Boolean).join(" / ") || "Unassigned";
  const orgByUser = {};
  if (orgMembers.length) {
    for (const m of orgMembers) orgByUser[m.id] = orgLabelFor(m.org);
  } else if (user?.org) {
    const own = orgLabelFor(user.org);
    orgByUser[user.id] = own;
    for (const m of unitMembers) orgByUser[m.id] = own;
  }
  const editTask = editId ? (tasks.find((t) => t.id === editId) || null) : null;

  if (!user?.company_id || !user?.role_id) {
    return <MembershipSetup title={user?.terminology?.task_label_plural || "Tasks"} message="To build your own tasks, join a company and pick your crew role. One-time setup." />;
  }

  return (
    <div data-testid="tasks-timeline-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">{`${user?.terminology?.task_label || "Task"} Planning`}</h1>
        <p className="text-slate-400 mt-1 text-sm">Build tasks with brief/post time — they extend your duty time for the day. See the timeline on the {user?.terminology?.task_label || "Task"} Timeline page; commit, authorise and post from the {user?.terminology?.task_label || "Task"} List page.</p>
      </div>

      <BuildTaskForm
        user={user} types={types} roles={roles} unitMembers={unitMembers} cfg={cfg}
        myEquip={myEquip} taskLocs={taskLocs} orgByUser={orgByUser}
        editTask={editTask} prefill={prefill}
        onSaved={(keepEdit) => { loadTasks(); if (!keepEdit) setEditId(null); }}
        onCancelEdit={() => setEditId(null)}
      />
    </div>
  );
}
